import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const LICENCE_IMAGE =
  'https://storage.helloreaddy.io/project_files/13c128fb-7e8c-4d26-83b3-9aecb22113ce/9bbdc3ae-ebfd-408a-9552-f5eb7265d3c5_compressed_B-VN-HA-TH-THAO-V-DU-LCH_page-0001.webp';

export default function LicenseSection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <section id="license" className="w-full bg-white py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <p className="text-accent-500 font-body text-sm tracking-wide uppercase mb-2">{t('about.licenseEyebrow')}</p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-secondary-900 leading-tight">
            {t('about.licenseTitleA')} <span className="text-accent-500">{t('about.licenseTitleB')}</span>
          </h2>
          <p className="text-secondary-500 text-sm md:text-base mt-3 max-w-xl mx-auto">
            {t('about.licenseSubtitle')}
          </p>
        </div>

        {/* Document frame */}
        <div className="max-w-3xl mx-auto">
          <div className="bg-background-50 border border-background-200 rounded-xl p-4 md:p-6">
            <div className="relative rounded-lg overflow-hidden border border-background-200 bg-white">
              <img
                src={LICENCE_IMAGE}
                alt={t('about.licenseAlt')}
                className="w-full h-[420px] md:h-[560px] object-contain bg-white"
              />
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-foreground-600 flex items-start gap-2 text-center sm:text-left leading-relaxed">
                <i className="ri-verified-badge-line text-primary-600 text-base shrink-0 mt-0.5" />
                <span>{t('about.licenseCaption')}</span>
              </p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer shrink-0"
              >
                <i className="ri-zoom-in-line" />
                {t('about.licenseView')}
              </button>
            </div>
            <div className="mt-5 pt-5 border-t border-background-200 text-center">
              <Link
                to="/why-us"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap group"
              >
                {t('about.licenseWhyLink')}
                <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {open && (
        <div
          className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4 md:p-8"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('about.licenseClose')}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <img
            src={LICENCE_IMAGE}
            alt={t('about.licenseAlt')}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
}